import { FileDown, FileSpreadsheet, FileText, Plus, RotateCcw, Trash2, AlertTriangle, Languages } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmt } from "@/lib/catalog";
import { exportPDF, exportXLSX, exportCSV, disclaimer, PLAN_BOX } from "@/lib/exports";
import { renderPlanImage } from "@/lib/reportPhoto";
import { sectionData, sectionsOf } from "@/lib/reportSection";
import { ReportOptions, reportOpts } from "./ReportOptions";
import { api } from "@/lib/api";
import { loadBranding, loadAppMark } from "@/lib/branding";
import { Section } from "./ui";
import { StaticsTable } from "./StaticsTable";
import { EngineReport } from "./EngineReport";
import { useState } from "react";

import { tr, getUiLang, withLangAsync, LANGS } from "@/lib/i18n";
import { computeBOM } from "@/lib/bom";
const Card = ({ label, value, unit, testid, accent }) => (
  <div className={`rounded-lg p-5 border sr-rise ${accent ? "bg-[#0F52BA] text-white border-[#0F52BA]" : "bg-white border-slate-200"}`}>
    <div className={`text-xs font-tech uppercase tracking-wider ${accent ? "text-blue-100" : "text-slate-500"}`}>{label}</div>
    <div className="font-display font-semibold text-3xl mt-1" data-testid={testid}>{value}<span className={`text-base ml-1 ${accent ? "text-blue-100" : "text-slate-400"}`}>{unit}</span></div>
  </div>
);

export default function StepResults({ p, update, layout, bom }) {
  const s = bom.stats;
  const [eng, setEng] = useState(null);
  const setQty = (item, v) => item.custom
    ? update((prev) => ({ bom_extra: prev.bom_extra.map((e, i) => (i === item.idx ? { ...e, qty: v } : e)) }))
    : update((prev) => ({ bom_overrides: { ...prev.bom_overrides, [item.code]: v } }));
  const setExtra = (idx, patch) => update((prev) => ({ bom_extra: prev.bom_extra.map((e, i) => (i === idx ? { ...e, ...patch } : e)) }));
  const addRow = () => update((prev) => ({ bom_extra: [...(prev.bom_extra || []), { code: "EXTRA", desc: tr("Articolo aggiuntivo"), qty: 1, unit: "pz" }] }));
  const run = async (fn, msg) => { try { await inRl(fn); toast.success(msg); } catch { toast.error(tr("Esportazione non riuscita")); } };
  const [pdfBusy, setPdfBusy] = useState(false);
  const [rl, setRl] = useState(getUiLang());
  const inRl = (fn) => withLangAsync(rl, async () => fn(computeBOM(p, layout)));
  // il calcolo strutturale viene sempre rilanciato, così binari e ganci nel PDF sono aggiornati
  const pdf = async () => {
    setPdfBusy(true);
    try {
      let res = eng;
      try {
        await api.put(`/projects/${p.id}`, { engine: p.engine || {}, roofs: p.roofs, modules: p.modules, building_height: p.building_height, altitude: p.altitude, roof_type: p.roof_type, background: p.background || {} });
        res = (await api.post(`/engine/calculate/${p.id}`, null, { headers: { "X-Lang": rl } })).data;
        if (rl === getUiLang()) setEng(res);
      } catch { toast.warning(tr("Calcolo strutturale non disponibile: la pianta su foto mostra solo i moduli")); }
      await inRl(async (rbom) => {
      const o = reportOpts(p);
      // sezioni dalle linee disegnate al passo Bozza moduli; senza linee: nel mezzo del campo della prima falda con moduli
      const placed = o.section && !layout.flat ? sectionsOf(layout) : [];
      const sf = layout.fields.find((f) => f.active.length);
      const sections = !o.section || layout.flat ? [] : placed.length
        ? placed.map((sc) => sectionData(sc.f, sc.u) && { ...sectionData(sc.f, sc.u), letter: sc.letter }).filter(Boolean)
        : [sf && sectionData(sf)].filter(Boolean);
      const plan = o.plan ? await renderPlanImage(p, layout, res?.drawing, PLAN_BOX.w / PLAN_BOX.h, { bg: o.plan_bg, dims: o.plan_dims, groupDims: o.plan_group_dims, sections: placed })
        .then((x) => x && { ...x, dims: o.plan_dims, groupDims: o.plan_group_dims, sections: placed.map((sc) => sc.letter), hasStruct: !!res?.drawing }).catch(() => null) : null;
      const brand = await loadBranding(p.id).catch(() => null);
      const appMark = brand?.name ? null : await loadAppMark();
      exportPDF(p, layout, rbom, res, { plan, sections, opts: o, brand, appMark });
      });
      toast.success(tr("Report PDF scaricato"));
    } catch { toast.error(tr("Esportazione non riuscita")); } finally { setPdfBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 sr-rise">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{tr("Risultati")}</h1>
          <p className="text-slate-500 mt-2">{tr("Riepilogo dell'impianto e distinta dei materiali modificabile.")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center h-11 rounded-md border border-slate-200 bg-white px-1 gap-1" title={tr("Lingua di report ed export")} data-testid="report-lang-switch">
            <Languages className="w-4 h-4 text-slate-400 mx-1" />
            {LANGS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setRl(k)} data-testid={`report-lang-${k}`}
                className={`h-8 px-2.5 rounded text-xs font-tech transition-colors ${rl === k ? "bg-[#0F52BA] text-white" : "text-slate-500 hover:bg-slate-100"}`}>{l}</button>
            ))}
          </div>
          <Button data-testid="export-pdf-btn" className="h-11 bg-[#0F52BA] hover:bg-[#0D47A1]" disabled={!layout.fields.length || pdfBusy} onClick={pdf}><FileDown className="w-4 h-4 mr-1.5" />{pdfBusy ? tr("Creazione PDF…") : tr("Report PDF")}</Button>
          <Button data-testid="export-xlsx-btn" variant="outline" className="h-11" onClick={() => run((rbom) => exportXLSX(p, rbom), tr("File Excel scaricato"))}><FileSpreadsheet className="w-4 h-4 mr-1.5" />{tr("Excel")}</Button>
          <Button data-testid="export-csv-btn" variant="outline" className="h-11" onClick={() => run((rbom) => exportCSV(p, rbom), tr("File CSV scaricato"))}><FileText className="w-4 h-4 mr-1.5" />CSV</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card accent label={tr("Potenza totale")} value={fmt(s.kwp)} unit="kWp" testid="result-kwp" />
        <Card label={tr("Moduli")} value={s.modules} unit={tr("pz")} testid="result-modules" />
        {layout.flat
          ? <Card label={tr("Zavorra")} value={fmt(s.ballastPerM2, 1)} unit={tr("kg/m²")} testid="result-ballast" />
          : <Card label={eng ? tr("Ganci (motore)") : tr("Ganci (stima)")} value={eng ? eng.bom?.hooks ?? "—" : s.hooks || 0} unit={eng ? tr("pz") : tr("pz · {0}/m²", [fmt(s.hooksPerM2)])} testid="result-hooks" />}
        <Card label={tr("Carico impianto")} value={fmt(s.perM2, 1)} unit={tr("kg/m²")} testid="result-load" />
      </div>
      <div className="grid md:grid-cols-3 gap-4 text-sm">
        <div className="bg-white border border-slate-200 rounded-lg p-4"><span className="text-slate-500">{tr("Superficie moduli")}</span><div className="font-tech font-medium mt-1">{fmt(s.moduleArea)} m²</div></div>
        <div className="bg-white border border-slate-200 rounded-lg p-4"><span className="text-slate-500">{eng ? tr("Vento / Neve (motore)") : tr("Vento / Neve (stima NTC 2018)")}</span><div className="font-tech font-medium mt-1" data-testid="result-loads">{eng ? `qp ${fmt(eng.trace.find((x) => x.id === "wind.qp")?.value, 3)} · sk ${fmt(eng.trace.find((x) => x.id === "snow.sk")?.value, 3)} kN/m²` : `qp ${fmt(p.env?.wind_load, 3)} · qs ${fmt(p.env?.snow_load, 3)} kN/m²`}</div></div>
        <div className="bg-white border border-slate-200 rounded-lg p-4"><span className="text-slate-500">{layout.flat ? tr("Zavorra totale (stima)") : tr("Interasse ganci (stima)")}</span><div className="font-tech font-medium mt-1">{layout.flat ? tr("{0} kg ({1} blocchi/modulo medi)", [fmt(s.ballastKg, 1), fmt(s.ballastPerModule, 1)]) : `${fmt(s.hookSpacing)} m`}</div></div>
      </div>
      <Section title={tr("Contenuto del report")} kicker={tr("PDF · viste e quote")}>
        <ReportOptions p={p} update={update} layout={layout} />
      </Section>
      <Section title={tr("Calcolo strutturale — motore backend")} kicker={tr("SIA 261 / SN EN · tracciabile")}>
        <EngineReport p={p} update={update} onResult={setEng} />
      </Section>
      {!eng && (
      <Section title={tr("Stima rapida NTC 2018 (indicativa, lato client — esegui il motore per il calcolo tracciabile)")} kicker={tr("NTC 2018 · EN 1991-1-4")}>
        <StaticsTable p={p} statics={bom.statics} flat={layout.flat} />
      </Section>
      )}
      <Section title={tr("Distinta dei materiali")} kicker="BOM">
        <div className="flex justify-end gap-2 -mt-10 mb-4">
          <Button size="sm" variant="ghost" data-testid="bom-reset-btn" onClick={() => update({ bom_overrides: {} })}><RotateCcw className="w-4 h-4 mr-1" />{tr("Ripristina calcolo")}</Button>
          <Button size="sm" variant="outline" data-testid="bom-add-row-btn" onClick={addRow}><Plus className="w-4 h-4 mr-1" />{tr("Aggiungi riga")}</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bom-table">
            <thead>
              <tr className="text-left text-xs font-tech uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">{tr("Codice")}</th><th className="py-2 pr-3">{tr("Descrizione")}</th><th className="py-2 pr-3 text-right">{tr("Calcolato")}</th><th className="py-2 pr-3 w-32">{tr("Quantità")}</th><th className="py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {bom.items.map((i) => (
                <tr key={i.custom ? `x${i.idx}` : i.code} className="border-b border-slate-100" data-testid={`bom-row-${i.custom ? `extra-${i.idx}` : i.code}`}>
                  <td className="py-2 pr-3 font-tech text-xs">{i.custom ? <Input className="h-9 w-28 font-tech text-xs" value={i.code} onChange={(e) => setExtra(i.idx, { code: e.target.value })} /> : i.code}</td>
                  <td className="py-2 pr-3">{i.custom ? <Input className="h-9" value={i.desc} onChange={(e) => setExtra(i.idx, { desc: e.target.value })} /> : i.desc}</td>
                  <td className="py-2 pr-3 text-right font-tech text-slate-400">{i.custom ? "—" : i.calc}</td>
                  <td className="py-2 pr-3">
                    <Input type="number" min={0} className={`h-9 font-tech ${!i.custom && i.qty !== i.calc ? "border-[#0F52BA] bg-blue-50" : ""}`} value={i.qty}
                      data-testid={`bom-qty-${i.custom ? `extra-${i.idx}` : i.code}`} onChange={(e) => setQty(i, e.target.value === "" ? 0 : Number(e.target.value))} />
                  </td>
                  <td className="py-2">{i.custom && <Button size="icon" variant="ghost" className="hover:text-amber-700" onClick={() => update((prev) => ({ bom_extra: prev.bom_extra.filter((_, k) => k !== i.idx) }))}><Trash2 className="w-4 h-4" /></Button>}</td>
                </tr>
              ))}
              {!bom.items.length && <tr><td colSpan={5} className="py-8 text-center text-slate-400">{tr("Nessun modulo posato: completa i passi 3 e 4.")}</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>
      <div className="flex gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="disclaimer">
        <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />{disclaimer()}
      </div>
    </div>
  );
}
